import React from 'react';
import { PROFILE_DATA } from '../data/config';
import { Github, Linkedin, Twitter, Heart, Coffee } from 'lucide-react';

const SOCIAL_LINKS = [
  { Icon: Github, key: 'github', label: 'GitHub' },
  { Icon: Linkedin, key: 'linkedin', label: 'LinkedIn' },
  { Icon: Twitter, key: 'twitter', label: 'Twitter' },
];

const FooterSocial = ({ Icon, label, href }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    className="group w-11 h-11 rounded-full flex items-center justify-center bg-cream-100/5 border border-cream-100/15 hover:border-caramel-400/50 hover:bg-cream-100/10 hover:shadow-[0_8px_20px_rgba(255,232,200,0.15)] transition-all duration-300"
  >
    <Icon className="w-5 h-5 text-cream-200 group-hover:text-cream-100 transition-colors" />
  </a>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative">
      {/* Soft gradient fade in from page → footer; replaces the hard top border */}
      <div
        className="absolute inset-x-0 top-0 h-px"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(201, 168, 117, 0.4), transparent)',
        }}
      />
      <div
        className="relative pt-12 pb-8"
        style={{
          background: 'linear-gradient(180deg, transparent 0%, rgba(20, 12, 8, 0.6) 25%, rgba(10, 6, 3, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* About */}
            <div>
              <h3 className="text-xl font-bold gradient-text mb-4">
                {PROFILE_DATA.name}
              </h3>
              <p className="text-cream-200/80 text-sm leading-relaxed">
                {PROFILE_DATA.title} passionate about automation and creating efficient solutions.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-xs font-semibold text-coffee-300 uppercase tracking-[0.2em] mb-4">Quick Links</h3>
              <div className="space-y-2">
                {['About', 'Experience', 'Projects', 'Contact'].map((link) => (
                  <a
                    key={link}
                    href={`#${link.toLowerCase()}`}
                    className="block text-sm text-cream-300 hover:text-caramel-300 hover:translate-x-1 transition-all duration-300"
                    style={{ display: 'inline-block' }}
                  >
                    {link}
                  </a>
                ))}
              </div>
            </div>

            {/* Social */}
            <div>
              <h3 className="text-xs font-semibold text-coffee-300 uppercase tracking-[0.2em] mb-4">Connect</h3>
              <div className="flex gap-3">
                {SOCIAL_LINKS.map(({ Icon, key, label }) => (
                  <FooterSocial
                    key={key}
                    Icon={Icon}
                    label={label}
                    href={PROFILE_DATA.social[key]}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 mt-8 border-t border-cream-100/10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-cream-300 text-sm text-center md:text-left">
                © {currentYear} {PROFILE_DATA.name}. All rights reserved.
              </p>
              <p className="text-cream-300 text-sm flex items-center gap-2">
                Made with
                <Heart
                  className="w-4 h-4 text-caramel-400 fill-current"
                  style={{ animation: 'heartbeat 2s ease-in-out infinite' }}
                />
                and lots of
                <Coffee className="w-4 h-4 text-caramel-400 inline" />
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
